import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

export default function Login() {
  const { user, loading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface-panel)',
        }}
      >
        <div className="stack-sm" style={{ alignItems: 'center' }}>
          <Loader2
            className="w-5 h-5 animate-spin"
            style={{ color: 'var(--icon-secondary)' }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading</span>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--surface-panel)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Wordmark */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
              marginBottom: 6,
            }}
          >
            Lightspark
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: '-0.6px',
              color: 'var(--text-primary)',
              lineHeight: 1.1,
            }}
          >
            Docket
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 450,
              color: 'var(--text-tertiary)',
              marginTop: 4,
            }}
          >
            Legal &amp; Compliance Ops
          </div>
        </div>

        {/* Card */}
        <div className="card">
          <div className="card-body" style={{ padding: 28 }}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: 4,
                }}
              >
                Sign in
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                Use your @lightspark.com Google account to continue.
              </div>
            </div>

            {error && (
              <div
                className="callout red"
                style={{ marginBottom: 16, padding: '10px 12px' }}
              >
                <AlertCircle
                  className="w-4 h-4"
                  style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }}
                />
                <div className="callout-body">
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-primary)' }}>
                    Authentication failed
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {error === 'auth_failed'
                      ? 'Only @lightspark.com email addresses are allowed.'
                      : 'An error occurred during login. Please try again.'}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={login}
              className="btn outline"
              style={{
                width: '100%',
                justifyContent: 'center',
                gap: 10,
                padding: '10px 14px',
                fontSize: 13,
                fontWeight: 450,
              }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            textAlign: 'center',
            marginTop: 16,
            fontSize: 11,
            color: 'var(--text-tertiary)',
            letterSpacing: '0.2px',
          }}
        >
          Restricted to @lightspark.com
        </div>
      </div>
    </div>
  );
}
